import React, { useEffect, useMemo, useState } from 'react'
import { getDailyMeetings } from '../services/api';
import styled from 'styled-components';
import { Color, Colors } from '../styles/Colors';
import SEKLogo from '../components/icons/SEKLogo';


const Wrapper = styled.div`
  display: grid; 
  grid-template-columns: minmax(32px, 1fr) [inner-start] minmax(0, 175rem) [inner-end] minmax(32px, 1fr);
`;

const Header = styled.div`
  display: flex;
  font-size: 54px;
  font-family: Roboto, sans-serif;

  div:nth-child(1) {
    flex: 50%; 
  } 

  div:nth-child(2),
  div:nth-child(3) {
    flex: 25%;  
  }  

  div:first-of-type {
    display: flex; 
    align-items: center;
  }
`;

const DateText = styled.div`
  & > span {
    font-size: 60px;
    padding: 0;
    font-family: Roboto, sans-serif;
    color: ${Color(Colors.black)}; 
    font-weight: 600; 

    &:first-of-type {
      &::after {
        content: ''; 
        border-right: 3px solid #0a6aa1; 
        margin-left: 20px; 
        padding: 0;
        }
      }

    &:last-of-type {
      margin-left: 20px;
    }
    
    @media all and (min-width: 1920px) {
      font-size: 62px; 
    }
      
    @media all and (max-width: 1200px) {
        font-size: 32px; 
    }
  }
`;
const Logo = styled.div`
  & > svg {
    width: 240px;
    
    @media all and (max-width: 1200px) {
      width: 132px;  
    }
  }

`;

const MeetingsContainer = styled.div`
  grid-column: inner; 
  display: flex;
  flex-direction: column;
  font-size: 24px;
  margin: 20px 0 20px 0;
`;

const ColumnTitles = styled.div`
  display: flex;
  justify-content: left; 
  align-items: center; 
  font-size: 54px;
  border-bottom: 3px solid #0a6aa1;
  font-family: Roboto, sans-serif;
  font-weight: 600; 
  padding: 1rem 0 1rem 1rem; 

  span:nth-child(1) {
    flex: 45%; 
  } 

  span:nth-child(2),
  span:nth-child(3) {
    flex: 27.5%;  
  } 

  @media all and (min-width: 1920px) {
    font-size: 62px; 
  }
    
  @media all and (max-width: 1200px) {
    font-size: 32px; 
    padding: .5rem 0 .5rem .5rem; 

  }
`;

const MeetingStyle = styled.div`
  display: flex;
  font-size: 36px;
  border-bottom: 3px solid #0a6aa1;
  font-family: Roboto, sans-serif; 
  padding: 1rem 0 1rem 1rem; 

  span {
    box-sizing: border-box; 
    padding-right: 2rem;    
    white-space: nowrap;
    flex: 50%;

    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    
    white-space: normal; 
    overflow: hidden;
    word-break: break-word; 
    overflow-wrap: anywhere;
  }

  span:nth-child(1) {
    flex: 45%;  
  } 

  span:nth-child(2),
  span:nth-child(3) {
    flex: 27.5%;  
  } 

  @media all and (min-width: 1920px) {
    font-size: 48px; 
  }
    
  @media all and (max-width: 1200px) {
    padding: .5rem 0 .5rem .5rem; 
    font-size: 28px; 
  }
`;

const NoMeetingsTitle = styled.p`
  font-size: 36px;
  padding-left: 1rem; 
  
  @media all and (min-width: 1920px) {
    font-size: 48px; 
  }

  @media all and (max-width: 1200px) {
    font-size: 30px; 
  }
`;

const OfficeMeetings = () => {
  const [meetings, setMeetings] = useState([]);
  const [today, setToday] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(0);

  const timeToDate = (timeStr, now = new Date()) => {
    // If timeStr is missing or "00:00" return null (meaning - no valid time)
    if (!timeStr || timeStr === '00:00') return null; 

    // Split "HH:mm" into numbers
    const [h, m] = String(timeStr).split(':').map(Number)
    const date = new Date(now); 
    
    // Set the clock (HH:mm) on date "now" (we need a full timestamp to compare later)
    date.setHours(h, m, 0, 0); 
    return date;
  }

  const visibleMeetings = useMemo(() => {
    const now = today; 
    return (Array.isArray(meetings) ? meetings : []).filter((meeting) => {
      const start = timeToDate(meeting.startdate, now)
      const end = timeToDate(meeting.enddate, now)

      // Keep meetings that does not has a proper start/endtime
      if (!start || !end) return true; 

      // Filter out all meetings that has passed "now"
      return end > now;
    })
  }, [meetings, today]); 

  // Show max 4 meetings per page 
  // Recalculate list when currentMeetings changes 
  const currentMeetings = useMemo(() => {
    if (visibleMeetings.length > 4) {
      const startPage = currentPage * 4;
      return visibleMeetings.slice(startPage, startPage + 4);
    } else {
      return visibleMeetings; 
    }
  }, [visibleMeetings, currentPage]); 
      
  useEffect(() => {
    let intervalId; 

    const timeoutId = setTimeout(() => {
      setToday(new Date());
      
      // Then keep updating every minute (2)
      intervalId = setInterval(() => {
        setToday(new Date()); 
      }, 1000 * 60) 
    }, (60 - new Date().getSeconds()) * 1000); // Wait until the next real minute, before starting the 1-minute interval (1)
    

    return () => {
      // Ensure the interval and timeout is not running while visiting other pages
      clearTimeout(timeoutId); 
      clearInterval(intervalId); 
    }
  }, []); 

  // Update list of meetings every minute
  useEffect(() => {
    let cancelled = false; 

    const getMeetings = async () => {
      try {
        const data = await getDailyMeetings();
        if (!cancelled) setMeetings(Array.isArray(data) ? data : []); 
      } catch (error) {
        console.log('getDailyMeetings failed', error); 
        if (!cancelled) setMeetings([]); 
      }
    }
    
    getMeetings();
    
    const intervalId = setInterval(() => {
      getMeetings();
    }, 1000 * 60); 

    return () => {
      cancelled = true; 
      clearInterval(intervalId); 
    }
  }, []);

  // Rotate pages when there are more than 4 meetings
  useEffect(() => {
    if (visibleMeetings.length <= 4) return;
    
    const intervalId = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % Math.ceil(visibleMeetings.length / 4)); 
    }, 5000)

    return () => clearInterval(intervalId); 

  }, [visibleMeetings.length]);

  // Reset to first page if ended meetings make the current page invalid
  useEffect(() => {
    const totalPages = Math.ceil(visibleMeetings.length / 4); 

    if (currentPage > 0 && (totalPages === 0 || currentPage >= totalPages)) {
      setCurrentPage(0); 
    }
  }, [visibleMeetings.length, currentPage]); 
  
  return (
    <Wrapper>
      <MeetingsContainer>
        <Header>
          <DateText>
            <span>{`${today.toLocaleDateString('sv')}`}</span>
            <span>{today.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</span>
          </DateText>
          <div></div>
          <Logo>
            <SEKLogo />
          </Logo>
        </Header>
        <ColumnTitles>
          <span>Group</span>
          <span>Room</span>
          <span>Time</span>
        </ColumnTitles>
        {Array.isArray(visibleMeetings) && visibleMeetings.length ? (
          currentMeetings.map((meeting, index) => (
            <MeetingStyle key={index}>
              <span>{meeting.committee}</span>
              <span>{meeting.room}</span>
              {meeting.startdate && meeting.enddate && meeting.startdate !== '00:00' ? (
                <span>{meeting.startdate}-{meeting.enddate}</span> 
              ) : (
                <span></span>
              )}
            </MeetingStyle>
          ))
        ) : (
          <NoMeetingsTitle>No meetings found for today</NoMeetingsTitle>
        )}
      </MeetingsContainer>
    </Wrapper>
  )
}

export default OfficeMeetings